import React, { useEffect, useMemo, useState } from "react";
import GlobalWrapper from "../../../../components/Wrapper/GlobalWrapper";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import CardSectionArea from "./CardSectionArea";
import StudentDetailsCard from "./StudentDetailsCard";
import { Box, Chip, Stack, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import useResponsive from "../../../../Hooks/useResponsive";
import { useDispatch } from "react-redux";
import { getAttendenceViewOfCorrespondingStudent, getAttendenceViewOfCorrespondingStudentDetails } from "../../../../redux/features/batchSlice";
import { DataGrid } from "@mui/x-data-grid";

const StudentAttendanceLanding = () => {
  const dispatch = useDispatch();
  const xlDown = useResponsive("down", "xl");
  const { loading, attendanceData, cardDetails,attendanceViewLoading } = useSelector((state) => state.batch);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const batchAssignmentId = queryParams.get("batchAssignmentId");
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("MM"));
  const [selectedyYear, setSelectedYear] = useState(dayjs().format("YYYY"));
  const dispatchParams = {
    batchAssignmentId: batchAssignmentId || null,
    month: selectedMonth,
    year: selectedyYear,
  };

  useEffect(() => {
    dispatch(getAttendenceViewOfCorrespondingStudentDetails(dispatchParams));
  }, []);
  useEffect(() => {
    if (batchAssignmentId) {
      dispatch(getAttendenceViewOfCorrespondingStudent(dispatchParams));
    }
  }, [batchAssignmentId, selectedMonth, selectedyYear]);

  const getDaysInMonth = (month, year) => {
    return dayjs(`${year}-${month}-01`).daysInMonth();
    // This will return the number of days in the specified month like 30 or 31
  };
  const daysInMonth = getDaysInMonth(selectedMonth, selectedyYear);

  const tableHead = useMemo(() => {
    return [
      { field: "subject", headerName: "Subject", minWidth: 150, disableColumnMenu: true, sortable: false },
      ...Array.from({ length: daysInMonth}, (_, i) => ({
        field: `day-${i + 1}`,
        headerName: `Day ${i + 1}`,
        minWidth: 155,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        sortable: false,

        renderCell: (params) => {
          const sessions = params.row.days[i]?.session || [];
          if (!sessions.length) {
            return (
              <Box sx={{ p: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.5 }}>
                  —
                </Typography>
              </Box>
            );
          }

          return (
            <Box sx={{ p: 1 }}>
              <Stack direction="column" spacing={1}>
                {sessions.map((session, index) => (
                  <Chip
                    key={index}
                    label={`Session ${session.session_no} • ${session.time}`}
                    size="small"
                    sx={{
                      backgroundColor: session.status === "present" ? "green" : "red",
                      color: "#fff",
                      fontSize: "0.7rem",
                      px: 1,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          );
        },
      })),
    ];
  }, [daysInMonth]);

  const rows = useMemo(() => attendanceData?.data?.schedule || [], [attendanceData]);
  return (
    <GlobalWrapper title={"Student Attendance"}>
      <CardSectionArea cardDatas={cardDetails?.cardDatas || []} />
      <Stack direction={xlDown ? "column" : "row"} spacing={2} mt={2} sx={{ alignItems: "center" }}>
        <StudentDetailsCard studentDetails={cardDetails?.studentDetails} />
        <Box sx={{ height: "webkit-fill-available", width: "100%", borderRadius: 1, p: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction="row" spacing={2}>
              <DatePicker
                label="Month"
                views={["month"]}
                value={dayjs(selectedMonth)}
                onChange={(newValue) => setSelectedMonth(dayjs(newValue).format("MM"))}
                slotProps={{ textField: { size: xlDown ? "small" : "small", fullWidth: true } }}
              />
              <DatePicker
                label="Year"
                views={["year"]}
                value={dayjs(selectedyYear)}
                onChange={(newValue) => setSelectedYear(dayjs(newValue).format("YYYY"))}
                slotProps={{ textField: { size: xlDown ? "small" : "small", fullWidth: true } }}
              />
            </Stack>
          </LocalizationProvider>
        </Box>
      </Stack>

      <Box mt={2}>
        <DataGrid
          rows={rows}
          columns={tableHead}
          getRowId={(row) => row?.subject}
          loading={attendanceViewLoading}
          disableRowSelectionOnClick
          autoHeight  
          getRowHeight={(params) => {
            const row = params.model;
            const maxSessions = Math.max(...row.days.map((day) => day.session?.length || 0));
            return 40 + maxSessions * 28;
          }}
          hideFooterPagination
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              background: "linear-gradient(125deg, #51475bff, #792191ff)",
              color: "#fff",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid #ccc",
              borderBottom: "1px solid #ccc",
            },
            "& .MuiDataGrid-row .MuiDataGrid-cell:first-of-type": {
              fontWeight: "bold",
            },
          }}
        />
      </Box>
    </GlobalWrapper>
  );
};

export default StudentAttendanceLanding;
